<template>
	<div :component="$options.name">
		<v-sheet class="pa-5">
			<v-fade-transition hide-on-leave>
				<div v-if="!organisationList">
					<p class="text-body-1 mb-3 text-center">
						Loading details
					</p>
					<v-progress-linear height="6" indeterminate />
				</div>
			</v-fade-transition>
			<v-fade-transition hide-on-leave>
				<v-sheet v-if="organisationList" class="mx-auto" max-width="1200">
					<v-row>
						<v-col cols="12" md="3">
							<p class="text-body-2 font-weight-bold mb-1">
								‘Yours to Sign and Return’ document
							</p>
						</v-col>
						<v-col cols="12" md="9">
							<p class="text-body-2 mb-3">
								The 'Yours to Sign and Return' document includes the customer declaration to sign as confirmation and acceptance of the advice given. Select and
								confirm to include this document with the suitability report.
							</p>
							<dynamic-form
								ref="dynamicForm"
								:submit-button-text="'Confirm Selection'"
								:form-schema="form('firm_writeroute_customer_declaration').value"
								:form-data="formData"
								:cta-classes="'mt-3'"
								:submit-button-loading="submitting"
								@dynamic-form-submit="submitSRCustomerDeclaration"
							/>
						</v-col>
					</v-row>

					<v-divider class="my-10" />

					<v-row>
						<v-col cols="12" md="3">
							<p class="text-body-2 font-weight-bold mb-1">
								Suitability Report Colour
							</p>
						</v-col>
						<v-col cols="12" md="9">
							<p class="text-body-2 mb-3">
								Set the colours of the document headers and sub-headers using your firm’s HEX colour code. Alternatively, you can also use the colour canvas or
								colour slider.
							</p>
							<dynamic-form
								ref="dynamicForm"
								:submit-button-text="'Confirm Suitability Report Colour'"
								:form-schema="form('firm_writeroute_color').value"
								:form-data="srFormData"
								:cta-classes="'mt-3'"
								:submit-button-loading="submitting"
								@dynamic-form-submit="submitSRColor"
							/>
						</v-col>
					</v-row>

					<v-divider class="my-10" />

					<v-row>
						<v-col cols="12" md="3">
							<p class="text-body-2 font-weight-bold mb-1">
								Company Logo
							</p>
						</v-col>
						<v-col cols="12" md="9">
							<p class="text-body-2 mb-3">
								Upload your company logo to display it in the top right corner of every page of the suitability report documents. Permitted file types are png and
								jpg, with a maximum file size of 2MB.
							</p>

							<div v-if="logoPreviewUrl" class="mb-3">
								<img :src="logoPreviewUrl" alt="Company Logo" style="max-height: 80px; max-width: 300px; display: block" />
								<v-btn text small color="error" class="mt-2 ml-n2" :loading="removingLogo" @click="removeLogo">
									Remove logo
								</v-btn>
							</div>
							<p v-else-if="logoScanPending" class="text-body-2 mb-3">
								Your logo is being processed and will be displayed here shortly.
							</p>

							<dynamic-form
								v-if="!logoPreviewUrl"
								:key="logoFormKey"
								:submit-button-text="'Confirm Company Logo'"
								:form-schema="form('firm_writeroute_logo').value"
								:form-data="logoFormData"
								:cta-classes="'mt-3'"
								:submit-button-loading="submitting"
								@dynamic-form-submit="submitSRLogo"
							/>
						</v-col>
					</v-row>

					<v-row v-if="allowedCompanySettings.includes('wr-direct-formatting')">
						<v-col cols="12" md="3">
							<p class="text-body-2 font-weight-bold mb-1">
								Suitability Report Direct Formatting
							</p>
						</v-col>
						<v-col cols="12" md="9">
							<p class="text-body-2 mb-3">
								Disable direct formatting in the suitability report to ensure all text follows the default Word styles, allowing templates to be applied correctly.
							</p>
							<dynamic-form
								ref="dynamicForm"
								:submit-button-text="'Confirm Selection'"
								:form-schema="form('firm_writeroute_direct_formatting').value"
								:form-data="formData"
								:cta-classes="'mt-3'"
								:submit-button-loading="submitting"
								@dynamic-form-submit="submitSRDirectFormatting"
							/>
						</v-col>
					</v-row>
				</v-sheet>
			</v-fade-transition>
		</v-sheet>

		<common-dialog-confirm ref="confirm" />
	</div>
</template>

<script>
	import DynamicForm from '@/component/common/base/dynamic-form';
	import CommonDialogConfirm from '@/component/common/dialog/confirm';
	import { ElementTools } from '@/utils';
	import { mapActions, mapGetters, mapState } from 'vuex';

	export default {
		name: 'app-firm-writeroute',
		components: { DynamicForm, CommonDialogConfirm },

		props: {},

		data() {
			return {
				loading: {},
				formData: {},
				srFormData: {},
				logoFormData: { logo: [] },
				logoFormKey: 0,
				logoPreviewUrl: null,
				logoScanPending: false,
				removingLogo: false,
				submitting: false
			};
		},

		computed: {
			...mapState('Account', ['organisation']),
			...mapState('AccountOrganisation', ['organisationList']),
			...mapGetters('CmsForm', ['form']),
			...mapGetters('CmsSite', ['allowedCompanySettings'])
		},

		watch: {
			'srFormData.color': {
				handler(value) {
					// temporary fix while there is no way to disable the alpha channel in the colorpicker component: https://github.com/vuetifyjs/vuetify/issues/9590
					if (value.toString().match(/#[a-zA-Z0-9]{8}/)) {
						this.srFormData.color = value.substr(0, 7);
					}
				},
				deep: true
			}
		},

		created() {
			this.loadForm('firmWriteroute');
			this.loadForm('firmWriterouteColor');
			this.loadForm('firmWriterouteCustomerDeclaration');
			this.loadForm('firmWriterouteDirectFormatting');
			this.loadForm('firmWriterouteLogo');
			this.fetchLogoPreview();
			this.loadOrganisationDetails(this.organisation.id).then(() => {
				this.formData = {
					suitabilityReportVersion: this.organisation?.data?.suitabilityReportVersion || '',
					customerDeclaration: this.organisation?.data?.customerDeclaration ?? true,
					disableDirectFormatting: this.organisation?.data?.suitabilityReportDisableDirectFormatting ?? false
				};
				this.srFormData = {
					color: this.organisation?.data?.suitabilityReportColor || ''
				};
			});
		},

		methods: {
			...mapActions('AccountOrganisation', ['loadOrganisationDetails', 'loadOrganisationLogoPresignedUrl', 'modifyOrganisation']),
			...mapActions('CmsForm', ['loadForm']),

			async submitSRColor() {
				this.submitting = true;
				this.modifyOrganisation({ id: this.organisation.id, data: { suitabilityReportColor: this.srFormData.color } })
					.then(() => {
						ElementTools.fireNotification(this.$el, 'success', 'WriteRoute Settings have been successfully updated');
					})
					.catch(() => {
						ElementTools.fireNotification(this.$el, 'error', 'Failed to update WriteRoute settings');
					})
					.finally(() => (this.submitting = false));
			},

			async submitSRCustomerDeclaration() {
				this.submitting = true;
				this.modifyOrganisation({ id: this.organisation.id, data: { customerDeclaration: this.formData.customerDeclaration } })
					.then(() => {
						ElementTools.fireNotification(this.$el, 'success', 'WriteRoute Settings have been successfully updated');
					})
					.catch(() => {
						ElementTools.fireNotification(this.$el, 'error', 'Failed to update WriteRoute settings');
					})
					.finally(() => (this.submitting = false));
			},

			async fetchLogoPreview(retries = 5) {
				try {
					const data = await this.loadOrganisationLogoPresignedUrl();
					this.logoPreviewUrl = data?.preSignedUrl || null;
					this.logoScanPending = false;
				} catch (error) {
					this.logoPreviewUrl = null;
					// A freshly uploaded logo returns 423 whilst it is virus scanned; retry so the preview appears once the scan clears
					if (error?.status === 423 && retries > 0) setTimeout(() => this.fetchLogoPreview(retries - 1), 2000);
					else this.logoScanPending = error?.status === 423;
				}
			},

			async submitSRLogo() {
				const logo = this.logoFormData.logo?.[0];
				if (!logo?.s3Key) return;

				this.submitting = true;
				this.modifyOrganisation({ id: this.organisation.id, data: { suitabilityReportLogo: logo.s3Key } })
					.then(() => {
						ElementTools.fireNotification(this.$el, 'success', 'WriteRoute Settings have been successfully updated');
						this.logoFormData = { logo: [] };
						this.logoFormKey += 1;
						this.fetchLogoPreview();
					})
					.catch(() => {
						ElementTools.fireNotification(this.$el, 'error', 'Failed to update WriteRoute settings');
					})
					.finally(() => (this.submitting = false));
			},

			removeLogo() {
				this.$refs.confirm
					.open('Remove Company Logo', 'Are you sure you wish to remove your company logo? Suitability report documents will be generated without a logo.')
					.then(() => {
						this.removingLogo = true;
						this.modifyOrganisation({ id: this.organisation.id, data: { suitabilityReportLogo: null } })
							.then(() => {
								ElementTools.fireNotification(this.$el, 'success', 'WriteRoute Settings have been successfully updated');
								this.logoPreviewUrl = null;
								this.logoScanPending = false;
							})
							.catch(() => {
								ElementTools.fireNotification(this.$el, 'error', 'Failed to update WriteRoute settings');
							})
							.finally(() => (this.removingLogo = false));
					})
					.catch(() => {});
			},

			async submitSRDirectFormatting() {
				this.submitting = true;
				this.modifyOrganisation({ id: this.organisation.id, data: { suitabilityReportDisableDirectFormatting: this.formData.disableDirectFormatting } })
					.then(() => {
						ElementTools.fireNotification(this.$el, 'success', 'WriteRoute Settings have been successfully updated');
					})
					.catch(() => {
						ElementTools.fireNotification(this.$el, 'error', 'Failed to update WriteRoute settings');
					})
					.finally(() => (this.submitting = false));
			}
		}
	};
</script>
